import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 200;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #pageLoadTime = Date.now();
  #initialHoverDelay = DEACTIVATE_DELAY; // Delay before allowing hover activation after page load

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    if (window.matchMedia('(min-width: 990px)').matches) {
      const menuItems = this.querySelectorAll('.menu-list__link');

      menuItems.forEach(item => {
        item.addEventListener('focus', (event) => {
          const modifiedEvent = this.createModifiedEvent(event);

          this.activate(modifiedEvent);
        });

        item.addEventListener('click', this.#openSubmenuFromSearch);
      });
    } else {
      const menuSubitems = this.querySelectorAll('.menu-list__subtitle');

      menuSubitems.forEach(item => {
        item.addEventListener('click', (event) => {
          const parentItem = item.parentElement;
          const sublist = parentItem?.querySelector('.menu-list__sublist-wrapper');

          if (sublist) {
            event.preventDefault();
            parentItem?.classList.toggle('open');
            sublist.classList.toggle('open');
          }
        });
      });
    }

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Create a modified event with the target set to the parent element
   * @param {Event} event
   * @returns {FocusEvent}
   */
  createModifiedEvent = (event) => {
    const modifiedEvent = new FocusEvent(event.type, {
      bubbles: event.bubbles,
      cancelable: event.cancelable,
      relatedTarget: event instanceof FocusEvent ? event.relatedTarget : null,
    });

    Object.defineProperty(modifiedEvent, 'target', {
      value: event.target instanceof Element ? event.target.parentElement : null,
      writable: false
    });

    return modifiedEvent;
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // The search panel takes over the dropdown space below the header, so hovering or
    // focusing the nav must not re-open the mega menu on top of it. Clicking is handled
    // separately by #openSubmenuFromSearch.
    if (searchModalOpen()) return;

    // Check if this is a pointer event and if enough time has passed since page load
    if (event.type === 'pointerenter') {
      const timeSincePageLoad = Date.now() - this.#pageLoadTime;
      if (timeSincePageLoad < this.#initialHoverDelay) {
        // Ignoring initial hover after page load
        return;
      }
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * While the search panel is open it covers the dropdown area, so clicking a nav item that has
   * a submenu should dismiss the panel and reveal that submenu instead of navigating away.
   * Items without a submenu keep their normal link behaviour.
   * @param {Event} event
   */
  #openSubmenuFromSearch = (event) => {
    const link = event.currentTarget;

    if (!(link instanceof HTMLElement) || link.getAttribute('aria-haspopup') !== 'true') return;
    if (!searchModalOpen()) return;

    // Also cancels the navigation via preventDefault
    closeSearchModal(event);

    // Skip the hover delay — the click is already an explicit request to open the submenu
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#activateHandler(this.createModifiedEvent(event));
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Force-close the active dropdown immediately, regardless of hover state.
   * Used by external components (e.g. the search modal) that need to dismiss
   * the menu when they take over the dropdown space below the header.
   */
  closeMenu = () => {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    const item = this.#state.activeItem;
    if (!item) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150));
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Whether the search modal is currently open.
 * @returns {boolean}
 */
function searchModalOpen() {
  return document.querySelector('.search-modal.active') != null;
}

/**
 * Close the search modal, if the search component is present.
 * @param {Event} [event] - Forwarded to the component, which calls preventDefault on it.
 */
function closeSearchModal(event) {
  const component = document.getElementById('main-search-component');

  if (component instanceof HTMLElement && typeof (/** @type {any} */ (component).closeSearchModal) === 'function') {
    /** @type {any} */ (component).closeSearchModal(event);
  }
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
